// ============================================================================
// HAI Reporting Tool - Admin Panel Page
// Main admin panel with tabbed interface for system management
// ============================================================================

import { useState } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { FacilityManagement } from '@/components/admin/FacilityManagement';
import { WardManagement } from '@/components/admin/WardManagement';
import { UserManagement } from '@/components/admin/UserManagement';
import { useAdminAccess } from '@/hooks/useAdminAccess';

type AdminTab = 'facilities' | 'wards' | 'users';

export function AdminPage() {
  const { isAdmin } = useAdminAccess(true);
  const [activeTab, setActiveTab] = useState<AdminTab>('facilities');

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You need administrator privileges to access this page.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'facilities' as AdminTab, label: 'Facilities', icon: '🏥' },
    { id: 'wards' as AdminTab, label: 'Wards', icon: '🛏️' },
    { id: 'users' as AdminTab, label: 'Users', icon: '👥' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 -mx-6 -mt-6 px-6 py-8 mb-6">
          <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
          <p className="text-primary-100 mt-2">
            Manage facilities, wards, and user permissions
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <span className="text-xl">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="py-4">
          {activeTab === 'facilities' && <FacilityManagement />}
          {activeTab === 'wards' && <WardManagement />}
          {activeTab === 'users' && <UserManagement />}
        </div>

        {/* Help Section */}
        <div className="card bg-primary-50 border border-primary-200">
          <h3 className="font-semibold text-primary-900 mb-2">Admin Panel Guide</h3>
          <ul className="text-sm text-primary-700 space-y-1 list-disc list-inside">
            <li><strong>Facilities:</strong> Create and manage healthcare facilities (hospitals, clinics)</li>
            <li><strong>Wards:</strong> Add wards to facilities (ICU, Medical Ward, etc.)</li>
            <li><strong>Users:</strong> Assign roles and wards to users for data access control</li>
            <li><strong>Roles:</strong> Admin (full access), IPC Nurse (infection control), Ward Nurse (data entry), Officer (view only)</li>
            <li><strong>Ward Access:</strong> Admins see all wards; other users only see assigned wards</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}
